import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { forkJoin } from 'rxjs';

import { GlobalsService } from '../../services/globals.service';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';

@Component({
	selector: 'app-xref-cost-upload',
	templateUrl: './xref-cost-upload.component.html',
	styleUrls: ['./xref-cost-upload.component.scss'],
})
export class XrefCostUploadComponent implements OnInit {
	files: File[] = [];
	UploadedHeaders: string[] = [];
	uploaddata: any = false;
	mapcols: { field: string; label: string; req: boolean }[] = [];
	mapped: { field: string; label: string; req: boolean }[] = [];
	mappingForm = new UntypedFormGroup({});
	rowNumber = new UntypedFormControl('1');
	color: any = 'blue';
	config: any = {};
	sending = false;
	preProcessing = false;
	mappeddata: any[] = [];
	preview = false;

	uploadHelp = false;
	processing_recs = false;
	reviewReady = false;

	readonly mappingPreviewMax = 50;
	/** After Pre-Process, only render this many preview rows to keep the DOM responsive. */
	readonly previewTableMax = 250;
	private static readonly quickcheckChunkSize = 750;

	private readonly allowedExtensions = ['.csv', '.xlsx', '.xls'];

	private readonly fieldHeaderSynonyms: Record<string, string[]> = {
		stockid: ['stockid', 'sku', 'itemnumber', 'itemnum', 'item', 'partnumber', 'partnum', 'stylenum', 'style', 'productcode', 'product'],
		mfgname: ['mfgname', 'brand', 'mfg', 'make', 'manufacturer', 'mfgcode'],
		mfgpart: ['mfgpart', 'mfg part', 'manufacturerpart', 'mpn', 'oem', 'partno', 'part number'],
		xrefcost: ['xrefcost', 'xref cost', 'xref_cost', 'externalcost', 'refcost', 'catalogcost', 'xref'],
	};

	constructor(
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private location: Location,
		private globalsService: GlobalsService,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.config = this.globalsService.getApiConfig();
		this.globalSearchService.configsubscription.subscribe((r: any) => {
			this.config = r;
		});
	}

	ngOnInit(): void {
		this.adjustMapping();
	}

	toggleUploadHelp(): void {
		this.uploadHelp = !this.uploadHelp;
	}

	formatFileSizeKb(bytes: number): string {
		return (bytes / 1024).toFixed(1);
	}

	get canPreProcess(): boolean {
		if (!this.mappingForm || this.mappingForm.invalid) {
			return false;
		}
		const v = this.mappingForm.value as Record<string, string>;
		if (!v.xrefcost) {
			return false;
		}
		const hasStock = !!v.stockid;
		const hasBrandPart = !!v.mfgname && !!v.mfgpart;
		return hasStock || hasBrandPart;
	}

	private normalizeForMatch(s: string): string {
		return String(s || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '');
	}

	private scoreFieldHeader(field: string, header: string): number {
		const hn = this.normalizeForMatch(header);
		if (!hn) {
			return 0;
		}
		const fn = this.normalizeForMatch(field);
		if (hn === fn) {
			return 100;
		}
		let best = 0;
		const syns = this.fieldHeaderSynonyms[field];
		if (syns) {
			for (const syn of syns) {
				const sn = this.normalizeForMatch(syn);
				if (!sn) {
					continue;
				}
				if (hn === sn) {
					best = Math.max(best, 92);
				} else if (hn.includes(sn) && sn.length >= 4) {
					best = Math.max(best, 72);
				} else if (hn.includes(sn) && sn.length >= 3) {
					best = Math.max(best, 52);
				} else if (sn.includes(hn) && hn.length >= 4) {
					best = Math.max(best, 58);
				}
			}
		}
		return best;
	}

	applyAutoMapping(options?: { replaceAll?: boolean; onlyEmpty?: boolean }): void {
		if (!this.fileColumnsForMapping.length || !this.mappingForm) {
			return;
		}
		const replaceAll = options?.replaceAll === true;
		const onlyEmpty = options?.onlyEmpty === true;
		if (replaceAll) {
			this.mapcols.forEach((m) => this.mappingForm.get(m.field)?.setValue(''));
		}
		const fieldIndex = (f: string) => this.mapcols.findIndex((x) => x.field === f);
		const pairs: { field: string; header: string; score: number }[] = [];
		for (const m of this.mapcols) {
			for (const h of this.fileColumnsForMapping) {
				const sc = this.scoreFieldHeader(m.field, h);
				if (sc >= 40) {
					pairs.push({ field: m.field, header: h, score: sc });
				}
			}
		}
		pairs.sort((a, b) => {
			if (b.score !== a.score) {
				return b.score - a.score;
			}
			return fieldIndex(a.field) - fieldIndex(b.field);
		});
		const usedHeaders = new Set<string>();
		if (!replaceAll) {
			this.mapcols.forEach((m) => {
				const v = this.mappingForm.get(m.field)?.value;
				if (v) {
					usedHeaders.add(String(v));
				}
			});
		}
		const usedFields = new Set<string>();
		for (const p of pairs) {
			if (usedHeaders.has(p.header) || usedFields.has(p.field)) {
				continue;
			}
			const ctrl = this.mappingForm.get(p.field);
			if (!ctrl) {
				continue;
			}
			if (onlyEmpty && ctrl.value) {
				continue;
			}
			ctrl.setValue(p.header);
			usedHeaders.add(p.header);
			usedFields.add(p.field);
		}
		this.cdr.markForCheck();
	}

	suggestMappings(): void {
		this.applyAutoMapping({ replaceAll: true });
		this.globalSearchService.showNotification('Suggested column mappings applied — review before Pre-Process.', 'info', 'bottom', 'left');
	}

	clearAllFiles(): void {
		this.files = [];
		this.resetParseState();
	}

	resetParseState(): void {
		this.processing_recs = false;
		this.reviewReady = false;
		this.uploaddata = false;
		this.UploadedHeaders = [];
		this.preview = false;
		this.preProcessing = false;
		this.mappeddata = [];
		this.adjustMapping();
	}

	backFromParse(): void {
		this.resetParseState();
	}

	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = () => reject(new Error('read_failed'));
			reader.readAsArrayBuffer(file);
		});
	}

	private parseSpreadsheet(file: File): Promise<{ rows: Record<string, string>[]; headers: string[] }> {
		return this.readFileAsArrayBuffer(file).then((buf) => {
			const wb = XLSX.read(buf, { type: 'array', cellDates: true, dense: false });
			const sheetName = wb.SheetNames[0];
			if (!sheetName) {
				return { rows: [], headers: [] };
			}
			const ws = wb.Sheets[sheetName];
			const headerRow0 = Math.max(0, parseInt(String(this.rowNumber.value ?? '1'), 10) - 1);
			const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
				range: headerRow0,
				defval: '',
				raw: false,
			});
			const rows: Record<string, string>[] = raw.map((row) => {
				const o: Record<string, string> = {};
				for (const k of Object.keys(row)) {
					if (k === '__rowNum__') {
						continue;
					}
					const v = row[k];
					o[k] = v === null || v === undefined ? '' : String(v).trim();
				}
				return o;
			});
			const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
			return { rows, headers };
		});
	}

	processFiles(options?: { userInitiated?: boolean }): void {
		const userInitiated = options?.userInitiated === true;
		if (!this.files.length) {
			if (userInitiated) {
				this.globalSearchService.showNotification('Add a file first.', 'info', 'bottom', 'left');
			}
			return;
		}
		const file = this.files[0];
		this.processing_recs = true;
		this.reviewReady = false;
		this.uploaddata = false;
		this.UploadedHeaders = [];
		this.preview = false;
		this.mappeddata = [];

		const run = () => {
			this.parseSpreadsheet(file)
				.then(({ rows, headers }) => ({ rows, headers, ok: true as const }))
				.catch(() => ({ rows: [] as Record<string, string>[], headers: [] as string[], ok: false as const }))
				.then((r) => {
					this.processing_recs = false;
					if (!r.ok) {
						this.globalSearchService.showNotification(
							`Could not read file: ${file.name}. Use CSV or Excel (.csv, .xlsx, .xls), first sheet.`,
							'danger',
							'bottom',
							'left'
						);
						return;
					}
					const name = file.name;
					const allFileData = r.rows.map((row) => ({ ...row, _sourceFile: name }));
					this.UploadedHeaders = r.headers.length ? [...r.headers] : [];
					if (allFileData.length === 0) {
						this.globalSearchService.showNotification(
							'No data rows found after the header row. Check the header row number.',
							'warning',
							'bottom',
							'left'
						);
						return;
					}
					this.uploaddata = allFileData;
					this.reviewReady = true;
					this.adjustMapping();
					if (userInitiated) {
						this.globalSearchService.showNotification(
							'Column mappings auto-suggested from headers — confirm, then Pre-Process.',
							'success',
							'bottom',
							'left'
						);
					}
				});
		};

		if (typeof requestAnimationFrame !== 'undefined') {
			requestAnimationFrame(() => run());
		} else {
			setTimeout(() => run(), 0);
		}
	}

	rereadFile(): void {
		this.processFiles({ userInitiated: true });
	}

	get dropzoneThemeColor(): string {
		const allowed = new Set(['blue', 'azure', 'purple', 'green', 'red']);
		const c = String(this.color || 'blue')
			.toLowerCase()
			.trim();
		return allowed.has(c) ? c : 'blue';
	}

	onSelect(event: NgxDropzoneChangeEvent): void {
		if (!event) {
			return;
		}
		if (event.rejectedFiles?.length) {
			const names = event.rejectedFiles.map((f) => f.name).join(', ');
			this.globalSearchService.showNotification(`Not added (type or size): ${names}`, 'warning', 'bottom', 'left');
		}
		if (!event.addedFiles?.length) {
			return;
		}
		const valid: File[] = [];
		const invalidNames: string[] = [];
		for (const f of event.addedFiles) {
			const lower = f.name.toLowerCase();
			if (this.allowedExtensions.some((ext) => lower.endsWith(ext))) {
				valid.push(f);
			} else {
				invalidNames.push(f.name);
			}
		}
		if (invalidNames.length) {
			this.globalSearchService.showNotification(
				`Only CSV and Excel are supported. Skipped: ${invalidNames.join(', ')}`,
				'warning',
				'bottom',
				'left'
			);
		}
		if (valid.length) {
			const first = valid[0];
			if (valid.length > 1) {
				this.globalSearchService.showNotification(`Only one file at a time — using: ${first.name}`, 'info', 'bottom', 'left');
			}
			if (this.reviewReady || this.uploaddata) {
				this.resetParseState();
			}
			this.files = [first];
			const runDrop = () => this.processFiles();
			if (typeof requestAnimationFrame !== 'undefined') {
				requestAnimationFrame(runDrop);
			} else {
				setTimeout(runDrop, 0);
			}
		}
	}

	onRemove(file: File): void {
		const index = this.files.indexOf(file);
		if (index !== -1) {
			this.files.splice(index, 1);
		}
		if (this.files.length === 0) {
			this.resetParseState();
		}
	}

	private buildMapColumnDefinitions(): { field: string; label: string; req: boolean }[] {
		return [
			{ field: 'stockid', label: 'Item number (optional if brand + part)', req: false },
			{ field: 'mfgname', label: 'Brand', req: false },
			{ field: 'mfgpart', label: 'Manufacturer part number', req: false },
			{ field: 'xrefcost', label: 'Xref cost', req: true },
		];
	}

	adjustMapping(): void {
		const prev: Record<string, string> = {};
		if (this.mappingForm && Object.keys(this.mappingForm.controls).length) {
			for (const key of Object.keys(this.mappingForm.controls)) {
				const v = this.mappingForm.get(key)?.value;
				if (v && this.fileColumnsForMapping.includes(String(v))) {
					prev[key] = String(v);
				}
			}
		}
		this.mapcols = this.buildMapColumnDefinitions();
		this.mappingForm = new UntypedFormGroup({});
		this.mapcols.forEach((m) => {
			const initVal = prev[m.field] ?? '';
			if (m.req === true) {
				this.mappingForm.addControl(m.field, new UntypedFormControl(initVal, [Validators.required]));
			} else {
				this.mappingForm.addControl(m.field, new UntypedFormControl(initVal, []));
			}
		});
		if (this.fileColumnsForMapping.length) {
			this.applyAutoMapping({ onlyEmpty: true });
		}
		this.cdr.markForCheck();
	}

	get fileColumnsForMapping(): string[] {
		return (this.UploadedHeaders || []).filter((k) => k !== '_sourceFile' && k !== '__rowNum__');
	}

	get mappingPreviewRows(): any[] {
		const rows = Array.isArray(this.uploaddata) ? this.uploaddata : [];
		return rows.slice(0, this.mappingPreviewMax);
	}

	get mappingPreviewTotal(): number {
		return Array.isArray(this.uploaddata) ? this.uploaddata.length : 0;
	}

	get previewTableRows(): any[] {
		const rows = Array.isArray(this.mappeddata) ? this.mappeddata : [];
		return rows.slice(0, this.previewTableMax);
	}

	get previewTableTotal(): number {
		return Array.isArray(this.mappeddata) ? this.mappeddata.length : 0;
	}

	getSystemFieldForFileHeader(fileHeader: string): string {
		const m = this.mapcols.find((mc) => this.mappingForm?.get(mc.field)?.value === fileHeader);
		return m ? m.field : '';
	}

	isFieldMappedToOtherHeader(fieldKey: string, currentFileHeader: string): boolean {
		const v = this.mappingForm?.get(fieldKey)?.value;
		return !!v && String(v) !== currentFileHeader;
	}

	onFileColumnMappingChange(fileHeader: string, newField: string): void {
		if (!this.mappingForm) {
			return;
		}
		this.mapcols.forEach((mc) => {
			const c = this.mappingForm.get(mc.field);
			if (c?.value === fileHeader) {
				c.setValue('');
			}
		});
		if (newField) {
			this.mappingForm.get(newField)?.setValue(fileHeader);
		}
		this.cdr.markForCheck();
	}

	downloadTemplate(): void {
		const csv = 'stockid,mfgname,mfgpart,xrefcost\n';
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, 'xref_cost_upload_template.csv');
	}

	/**
	 * After quickcheck, each row has `stockid` set (from file or resolved from brand + part).
	 * Preview always shows that system item # first when it was not a mapped file column.
	 */
	get previewColumns(): { field: string; label: string }[] {
		if (!this.preview || !this.mapped.length) {
			return this.mapped;
		}
		const hasStockidFromFile = this.mapped.some((m) => m.field === 'stockid');
		const systemLabel = 'Item # (system)';
		if (hasStockidFromFile) {
			return this.mapped.map((m) => (m.field === 'stockid' ? { field: 'stockid', label: systemLabel } : m));
		}
		return [{ field: 'stockid', label: systemLabel }, ...this.mapped];
	}

	remap(): void {
		this.preview = false;
	}

	runPreProcess(upload: Record<string, unknown>[]): void {
		if (this.mappingForm.invalid) {
			this.globalSearchService.showNotification('Map the xref cost column (required).', 'warning', 'bottom', 'left');
			return;
		}
		if (!this.canPreProcess) {
			this.globalSearchService.showNotification(
				'Map item number, or map both brand and manufacturer part number.',
				'warning',
				'bottom',
				'left'
			);
			return;
		}
		this.preview = true;
		const mapping = this.mappingForm.value as Record<string, string>;
		this.mapped = [];
		Object.entries(mapping).forEach(([key, value]) => {
			if (value !== '') {
				const col = this.mapcols.find((map) => map.field === key);
				if (col) {
					this.mapped.push(col);
				}
			}
		});
		this.mappeddata = [];
		upload.forEach((e: Record<string, unknown>) => {
			const line: Record<string, unknown> = { problem: '' };
			Object.entries(mapping).forEach(([key, value]) => {
				if (value !== '') {
					const cell = e[value];
					line[key] = cell === null || cell === undefined ? '' : String(cell).trim();
				}
			});
			this.mappeddata.push(line);
		});

		const lines = this.mappeddata;
		const chunk = XrefCostUploadComponent.quickcheckChunkSize;
		this.preProcessing = true;

		const applyResults = (results: any[]) => {
			this.mappeddata = results || [];
			this.mappeddata.forEach((e: any) => {
				if (e.problem == null || e.problem === undefined) {
					e.problem = '';
				}
			});
			this.preProcessing = false;
			this.cdr.markForCheck();
		};

		const onErr = () => {
			this.preview = false;
			this.preProcessing = false;
			this.globalSearchService.showNotification('Pre-process failed. Try again or check your connection.', 'danger', 'bottom', 'left');
			this.cdr.markForCheck();
		};

		if (lines.length <= chunk) {
			this.inventoryService.xrefcostUploadQuickCheck({ lines }).subscribe({
				next: applyResults,
				error: onErr,
			});
			return;
		}

		const requests = [];
		for (let i = 0; i < lines.length; i += chunk) {
			requests.push(this.inventoryService.xrefcostUploadQuickCheck({ lines: lines.slice(i, i + chunk) }));
		}
		forkJoin(requests).subscribe({
			next: (parts: any[][]) => {
				applyResults(parts.flat());
			},
			error: onErr,
		});
	}

	submit(): void {
		const lines = this.mappeddata
			.filter((e: any) => !e.problem || String(e.problem).trim() === '')
			.map((e: any) => ({
				stockid: String(e.stockid ?? '').trim(),
				xrefcost: e.xrefcost,
			}))
			.filter((e) => e.stockid !== '');
		if (!lines.length) {
			this.globalSearchService.showNotification('No valid rows to upload.', 'warning', 'bottom', 'left');
			return;
		}
		this.sending = true;
		const procChunk = 500;
		if (lines.length <= procChunk) {
			this.inventoryService.xrefcostUploadProcess({ lines }).subscribe({
				next: (r: any) => this.finishSubmit(r),
				error: () => {
					this.sending = false;
				},
			});
			return;
		}
		const parts: { stockid: string; xrefcost: unknown }[][] = [];
		for (let i = 0; i < lines.length; i += procChunk) {
			parts.push(lines.slice(i, i + procChunk));
		}
		forkJoin(parts.map((p) => this.inventoryService.xrefcostUploadProcess({ lines: p }))).subscribe({
			next: (results: any[]) => {
				const merged = results.reduce(
					(acc, r) => ({
						updated: acc.updated + (r.updated ?? 0),
						skipped: acc.skipped + (r.skipped ?? 0),
						errors: acc.errors + (r.errors ?? 0),
					}),
					{ updated: 0, skipped: 0, errors: 0 }
				);
				this.finishSubmit(merged);
			},
			error: () => {
				this.sending = false;
			},
		});
	}

	private finishSubmit(r: { updated?: number; skipped?: number; errors?: number }): void {
		this.sending = false;
		const msg = `Updated: ${r.updated ?? 0}. Skipped: ${r.skipped ?? 0}. Errors: ${r.errors ?? 0}.`;
		this.mappeddata = [];
		this.uploaddata = false;
		this.files = [];
		this.resetParseState();
		this.globalSearchService.showNotification(msg, 'success', 'bottom', 'left');
	}

	back(): void {
		this.location.back();
	}
}
